import { Fragment, useState } from 'react';
import { can, formatJalali, formatRial, toFaDigits } from '@kia/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { rowButton } from '../lib/rowButton';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  ErrorRetry,
  Field,
  Input,
  JalaliDateInput,
  NumberInput,
  useToast,
} from '../ui';

/**
 * وام‌های بانکی (CIRCULARITY_360 Phase B) — create a loan (its amortisation schedule is generated
 * server-side and stored once), see the schedule, and mark installments paid. Scheduled installments
 * feed the cash calendar; paying one (optionally later with a proof photo) removes it from the
 * projection. Read-only for users without `cashflow.write`.
 */

interface LoanListItem {
  id: number;
  lender: string;
  principal: number;
  annual_rate: number;
  term_months: number;
  method: 'annuity' | 'equal_principal';
  start_date: string;
  paidCount: number;
  remainingPrincipal: number;
  nextDue: string | null;
}
interface ScheduleRow {
  seq: number;
  due_date: string;
  principal_part: number;
  interest_part: number;
  status: 'scheduled' | 'paid';
  paid_by: string | null;
}
interface LoanDetail {
  loan: LoanListItem;
  schedule: ScheduleRow[];
}

const pct = (r: number) => toFaDigits((r * 100).toFixed(1)) + '٪';

export function LoansPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user ? can(user.role, 'cashflow.write') : false;
  const { data, status, error, reload } = useApiResource<LoanListItem[]>('/api/loans');
  const loans = data ?? [];
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<LoanDetail | null>(null);
  const [adding, setAdding] = useState(false);

  // add-loan form state
  const [lender, setLender] = useState('');
  const [principal, setPrincipal] = useState(0);
  const [ratePct, setRatePct] = useState(23);
  const [term, setTerm] = useState(12);
  const [method, setMethod] = useState<'annuity' | 'equal_principal'>('annuity');
  const [startDate, setStartDate] = useState('');
  const [busy, setBusy] = useState(false);

  async function open(id: number) {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    const r = await api<LoanDetail>(`/api/loans/${id}`);
    if (r.ok) setDetail(r.body as LoanDetail);
  }

  async function submit() {
    if (!(principal > 0) || !(term > 0) || !startDate) {
      toast('اصل وام، تعداد اقساط و تاریخ اولین قسط لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/loans', {
      method: 'POST',
      body: JSON.stringify({
        lender,
        principal,
        annual_rate: ratePct / 100,
        term_months: term,
        method,
        start_date: startDate,
      }),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ثبت وام', 'error');
      return;
    }
    toast('وام ثبت شد ✓');
    setAdding(false);
    setLender('');
    setPrincipal(0);
    setStartDate('');
    reload();
  }

  async function pay(loanId: number, seq: number, paid: boolean) {
    const r = await api<LoanDetail>(`/api/loans/${loanId}/pay`, {
      method: 'POST',
      body: JSON.stringify({ seq, paid }),
    });
    if (!r.ok) {
      toast('خطا در به‌روزرسانی قسط', 'error');
      return;
    }
    setDetail(r.body as LoanDetail);
    toast(paid ? 'قسط پرداخت‌شده ثبت شد' : 'پرداخت قسط لغو شد');
    reload();
  }

  return (
    <Card title="وام‌های بانکی">
      <p className="muted" style={{ marginTop: 0 }}>
        اقساط برنامه‌ریزی‌شده به‌صورت خودکار در «تقویم نقدینگی» به‌عنوان خروجی لحاظ می‌شوند. با
        پرداخت هر قسط، آن قسط از پیش‌بینی حذف می‌شود.
      </p>

      {canWrite ? (
        adding ? (
          <div
            className="row"
            style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}
          >
            <Field label="بانک / وام‌دهنده">
              <Input value={lender} onChange={(e) => setLender(e.target.value)} />
            </Field>
            <Field label="اصل وام (﷼)">
              <div style={{ width: 180 }}>
                <NumberInput value={principal} onValue={setPrincipal} />
              </div>
            </Field>
            <Field label="نرخ سود سالانه (٪)">
              <div style={{ width: 110 }}>
                <NumberInput value={ratePct} onValue={setRatePct} allowDecimal />
              </div>
            </Field>
            <Field label="تعداد اقساط (ماه)">
              <div style={{ width: 110 }}>
                <NumberInput value={term} onValue={setTerm} />
              </div>
            </Field>
            <Field label="روش">
              <select
                className="input"
                value={method}
                onChange={(e) => setMethod(e.target.value as 'annuity' | 'equal_principal')}
              >
                <option value="annuity">اقساط مساوی</option>
                <option value="equal_principal">اصل مساوی (نزولی)</option>
              </select>
            </Field>
            <Field label="تاریخ اولین قسط">
              <JalaliDateInput value={startDate} onValue={setStartDate} />
            </Field>
            <Button onClick={submit} loading={busy}>
              ثبت وام
            </Button>
            <Button variant="secondary" onClick={() => setAdding(false)} disabled={busy}>
              انصراف
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={() => setAdding(true)} style={{ marginBottom: 12 }}>
            + وام جدید
          </Button>
        )
      ) : null}

      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : loans.length === 0 ? (
        <p className="muted">وامی ثبت نشده.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>وام‌دهنده</th>
                <th>اصل</th>
                <th>نرخ سالانه</th>
                <th>مانده اصل</th>
                <th>اقساط پرداخت‌شده</th>
                <th>قسط بعدی</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => (
                <Fragment key={l.id}>
                  <tr {...rowButton(() => open(l.id))} style={{ cursor: 'pointer' }}>
                    <td>
                      {openId === l.id ? '▾ ' : '▸ '}
                      {l.lender || '—'}
                    </td>
                    <td className="num">{formatRial(l.principal)}</td>
                    <td className="num">{pct(l.annual_rate)}</td>
                    <td className="num">{formatRial(l.remainingPrincipal)}</td>
                    <td className="num">
                      {toFaDigits(String(l.paidCount))}/{toFaDigits(String(l.term_months))}
                    </td>
                    <td>{l.nextDue ? formatJalali(l.nextDue) : 'تسویه‌شده'}</td>
                  </tr>
                  {openId === l.id && detail ? (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--panel, #fafafa)' }}>
                        <table className="table" style={{ margin: 0 }}>
                          <thead>
                            <tr>
                              <th>قسط</th>
                              <th>سررسید</th>
                              <th>اصل</th>
                              <th>سود</th>
                              <th>مبلغ قسط</th>
                              <th>وضعیت</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.schedule.map((s) => (
                              <tr key={s.seq}>
                                <td>{toFaDigits(String(s.seq))}</td>
                                <td>{formatJalali(s.due_date)}</td>
                                <td className="num">{formatRial(s.principal_part)}</td>
                                <td className="num">{formatRial(s.interest_part)}</td>
                                <td className="num">
                                  {formatRial(s.principal_part + s.interest_part)}
                                </td>
                                <td>
                                  {s.status === 'paid' ? (
                                    <span className="chip" style={{ color: 'var(--green)' }}>
                                      پرداخت‌شده
                                      {canWrite ? (
                                        <button
                                          type="button"
                                          onClick={() => pay(l.id, s.seq, false)}
                                          style={{
                                            border: 0,
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            marginRight: 6,
                                            color: 'var(--muted)',
                                          }}
                                          aria-label="بازگردانی به پرداخت‌نشده"
                                        >
                                          ↺
                                        </button>
                                      ) : null}
                                    </span>
                                  ) : canWrite ? (
                                    <Button
                                      variant="secondary"
                                      onClick={() => pay(l.id, s.seq, true)}
                                    >
                                      ثبت پرداخت
                                    </Button>
                                  ) : (
                                    <span className="muted">برنامه‌ریزی‌شده</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
